import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Button, Card, Form, Input, InputNumber, Modal, Popconfirm,
  Space, Switch, Table, Tabs, Typography, Upload, message,
} from "antd";
import { DeleteOutlined, DownloadOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import ImmediateTimePicker from "../components/ImmediateTimePicker";
import axios from "../api/axios";

const WEEKDAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"];
const LABELS = { MONDAY: "Montag", TUESDAY: "Dienstag", WEDNESDAY: "Mittwoch", THURSDAY: "Donnerstag", FRIDAY: "Freitag" };
const TIME_FIELDS = [
  ["outboundPickup", "Abholung Wohnort"],
  ["outboundArrival", "Ankunft Einrichtung"],
  ["returnPickup", "Abholung Einrichtung"],
  ["returnArrival", "Ankunft Wohnort"],
];

const clearRouteCache = () => {
  // Der letzte gültige Fahrplan bleibt verfügbar, bis eine neue zulässige
  // Optimierung abgeschlossen wurde.
  localStorage.removeItem("routing.runState");
};

function WindowPicker({ value = {}, onChange, disabledFields = [] }) {
  return (
    <Space.Compact block>
      <ImmediateTimePicker
        format="HH:mm" minuteStep={5} placeholder="von" allowClear
        disabled={disabledFields.includes("start")}
        value={value.start}
        onChange={(v) => onChange?.({ ...value, start: v })}
        style={{ width: "50%" }}
      />
      <ImmediateTimePicker
        format="HH:mm" minuteStep={5} placeholder="bis" allowClear
        disabled={disabledFields.includes("end")}
        value={value.end}
        onChange={(v) => onChange?.({ ...value, end: v })}
        style={{ width: "50%" }}
      />
    </Space.Compact>
  );
}

const apiToRows = (windows = []) => WEEKDAYS.map((weekday) => {
  const w = windows.find((item) => item.weekday === weekday) || {};
  const row = { key: weekday, weekday };
  TIME_FIELDS.forEach(([prefix]) => { row[prefix] = { start: w[`${prefix}Start`], end: w[`${prefix}End`] }; });
  return row;
});

const rowsToApi = (rows) => rows.map((row) => {
  const value = { weekday: row.weekday };
  TIME_FIELDS.forEach(([prefix]) => {
    value[`${prefix}Start`] = row[prefix]?.start || null;
    value[`${prefix}End`] = row[prefix]?.end || null;
  });
  return value;
});

export default function PlanningSettingsPage() {
  const [rows, setRows] = useState([]);
  const [stopBufferMinutes, setStopBufferMinutes] = useState(0);
  const [maxRideEnabled, setMaxRideEnabled] = useState(false);
  const [maxRideMinutes, setMaxRideMinutes] = useState(60);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("");
  const savedPayload = useRef(null);
  const saveQueue = useRef(Promise.resolve());
  const saveRevision = useRef(0);
  const [facilityOpen, setFacilityOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [facilityForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      await saveQueue.current;
      const [settingsRes, facilitiesRes] = await Promise.all([
        axios.get("/planning-settings"), axios.get("/facilities"),
      ]);
      savedPayload.current = JSON.stringify({
        maxRideMinutes: settingsRes.data?.maxRideMinutes > 0 ? settingsRes.data.maxRideMinutes : null,
        dailyWindows: rowsToApi(apiToRows(settingsRes.data?.dailyWindows)),
        stopBufferMinutes: settingsRes.data?.stopBufferMinutes ?? 0,
      });
      setRows(apiToRows(settingsRes.data?.dailyWindows));
      setStopBufferMinutes(settingsRes.data?.stopBufferMinutes ?? 0);
      setMaxRideEnabled(Number(settingsRes.data?.maxRideMinutes) > 0);
      setMaxRideMinutes(settingsRes.data?.maxRideMinutes || 60);
      setFacilities(facilitiesRes.data || []);
    } catch { message.error("Planungsvorgaben konnten nicht geladen werden."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateRow = (weekday, field, value) => setRows((prev) => prev.map((row) =>
    row.weekday === weekday ? { ...row, [field]: value } : row));

  const columns = useMemo(() => [
    { title: "Wochentag", dataIndex: "weekday", width: 120, render: (v) => <b>{LABELS[v]}</b> },
    ...TIME_FIELDS.map(([field, label]) => ({
      title: label, dataIndex: field, width: 220,
      render: (value, row) => <WindowPicker value={value} onChange={(v) => updateRow(row.weekday, field, v)} />,
    })),
  ], []);

  useEffect(() => {
    if (loading || savedPayload.current === null) return;
    if (maxRideEnabled && (maxRideMinutes == null || maxRideMinutes < 1)) return;
    const payload = {
      maxRideMinutes: maxRideEnabled ? maxRideMinutes : null,
      dailyWindows: rowsToApi(rows), stopBufferMinutes,
    };
    const serialized = JSON.stringify(payload);
    if (serialized === savedPayload.current) return;
    savedPayload.current = serialized;
    const revision = ++saveRevision.current;
    setSaveStatus("saving");
    saveQueue.current = saveQueue.current.then(async () => {
      try {
        await axios.put("/planning-settings", payload);
        clearRouteCache();
        if (revision === saveRevision.current) setSaveStatus("saved");
      } catch (err) {
        if (revision === saveRevision.current) {
          savedPayload.current = "";
          setSaveStatus("error");
        }
        message.error(err?.response?.data?.message || "Automatisches Speichern fehlgeschlagen.");
      }
    });
  }, [loading, rows, maxRideEnabled, maxRideMinutes, stopBufferMinutes]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      await saveQueue.current;
      const res = await axios.get("/planning-settings/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = "planungsvorgaben.xlsx";
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      message.success("Planungsvorgaben wurden exportiert.");
    } catch { message.error("Excel-Export fehlgeschlagen."); }
    finally { setExporting(false); }
  };

  const importExcel = async ({ file, onSuccess, onError }) => {
    const data = new FormData(); data.append("file", file);
    setImporting(true);
    try {
      await saveQueue.current;
      const res = await axios.post("/planning-settings/import", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      clearRouteCache();
      await load();
      message.success(`${res.data.standardRows} Standardtage importiert, ${res.data.createdFacilities} Einrichtungen angelegt und ${res.data.updatedFacilities} aktualisiert.`);
      onSuccess?.(res.data);
    } catch (err) {
      message.error(err?.response?.data?.message || "Excel-Import fehlgeschlagen.");
      onError?.(err);
    } finally { setImporting(false); }
  };

  const copyMonday = () => {
    const monday = rows.find((row) => row.weekday === "MONDAY");
    if (!monday) return;
    setRows(rows.map((row) => row.weekday === "MONDAY" ? row : {
      ...row, ...Object.fromEntries(TIME_FIELDS.map(([field]) => [field, { ...monday[field] }])),
    }));
  };

  const openFacility = (facility) => {
    setEditingFacility(facility || null);
    setFacilityOpen(true);
    const values = { name: facility?.name || "", address: facility?.address || "", notes: facility?.notes || "" };
    WEEKDAYS.forEach((weekday) => {
      const w = facility?.dailyWindows?.find((item) => item.weekday === weekday) || {};
      values[`outbound_${weekday}`] = { start: w.outboundArrivalStart, end: w.outboundArrivalEnd };
      values[`return_${weekday}`] = { start: w.returnPickupStart, end: w.returnPickupEnd };
    });
    facilityForm.setFieldsValue(values);
  };

  const saveFacility = async (values) => {
    const payload = { name: values.name, address: values.address, notes: values.notes, dailyWindows: WEEKDAYS.map((weekday) => ({
      weekday,
      outboundArrivalStart: values[`outbound_${weekday}`]?.start || null,
      outboundArrivalEnd: values[`outbound_${weekday}`]?.end || null,
      returnPickupStart: values[`return_${weekday}`]?.start || null,
      returnPickupEnd: values[`return_${weekday}`]?.end || null,
    })) };
    try {
      if (editingFacility) await axios.put(`/facilities/${editingFacility.id}`, payload);
      else await axios.post("/facilities", payload);
      clearRouteCache();
      message.success("Einrichtung gespeichert.");
      setFacilityOpen(false); facilityForm.resetFields(); await load();
    } catch (err) { message.error(err?.response?.data?.message || "Einrichtung konnte nicht gespeichert werden."); }
  };

  const facilityColumns = [
    { title: "Einrichtung", dataIndex: "name" },
    { title: "Adresse", dataIndex: "address" },
    {
      title: "Zugeordnete Fahrgäste",
      dataIndex: "assignedPassengerCount",
      width: 190,
      align: "center",
      sorter: (a, b) => Number(a.assignedPassengerCount || 0) - Number(b.assignedPassengerCount || 0),
      render: (value) => <b>{Number(value || 0)}</b>,
    },
    { title: "Eigene Zeitfenster", render: (_, f) => `${f.dailyWindows?.length || 0} Wochentage` },
    { title: "", width: 180, render: (_, f) => <Space>
      <Button onClick={() => openFacility(f)}>Bearbeiten</Button>
      <Popconfirm
        title="Einrichtung löschen?"
        description="Zugeordnete Fahrgäste behalten ihre Arbeitsadresse, die Verknüpfung wird entfernt."
        onConfirm={async () => {
        try { await axios.delete(`/facilities/${f.id}`); clearRouteCache(); message.success("Einrichtung gelöscht und Fahrgast-Verknüpfungen entfernt."); load(); }
        catch (err) { message.error(err?.response?.data?.message || "Löschen fehlgeschlagen."); }
      }}><Button danger icon={<DeleteOutlined />} /></Popconfirm>
    </Space> },
  ];

  const standardTab = <Space direction="vertical" size={16} style={{ width: "100%" }}>
    <Alert showIcon type="info" message="Diese Zeitfenster gelten, sofern bei Einrichtung oder Fahrgast keine abweichenden Zeiten hinterlegt sind." />
    <Space><Button onClick={copyMonday}>Montag auf Dienstag bis Freitag übertragen</Button></Space>
    <Table rowKey="weekday" columns={columns} dataSource={rows} pagination={false} scroll={{ x: 1050 }} loading={loading} />
    <Card size="small" title="Maximale Fahrzeit als Standard">
      <Space wrap>
        <Switch checked={maxRideEnabled} onChange={setMaxRideEnabled} />
        <span>Standardwert für Fahrgäste ohne individuelle maximale Fahrzeit</span>
        <InputNumber min={1} max={360} value={maxRideMinutes} disabled={!maxRideEnabled} onChange={setMaxRideMinutes} addonAfter="Minuten" />
      </Space>
    </Card>
    <Card size="small" title="Zeitpuffer pro Stopp">
      <Space wrap>
        <label htmlFor="stop-buffer-minutes">Zusätzliche Zeit je Abhol- und Ankunftsstopp</label>
        <InputNumber id="stop-buffer-minutes" min={0} max={120} precision={0}
          value={stopBufferMinutes} onChange={(value) => setStopBufferMinutes(value ?? 0)} addonAfter="Minuten" />
      </Space>
      <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
        Der Puffer wird zusätzlich zu individuellen Umsteigezeiten eingeplant und bei Fahrplänen,
        Zeitfenstern und zeitabhängigen Kosten berücksichtigt. Mehrere Fahrgäste am selben
        aufeinanderfolgenden Halt erhalten den Puffer gemeinsam einmal. 0 Minuten bedeutet kein zusätzlicher Puffer.
      </Typography.Paragraph>
    </Card>
    <Typography.Text type={saveStatus === "error" ? "danger" : "secondary"} role="status">
      {saveStatus === "saving" ? "Wird automatisch gespeichert …" : saveStatus === "error"
        ? "Speichern fehlgeschlagen. Bitte erneut versuchen." : saveStatus === "saved"
        ? "Alle Änderungen gespeichert." : "Änderungen werden automatisch gespeichert."}
    </Typography.Text>
    {saveStatus === "error" && <Button onClick={() => setRows((prev) => [...prev])}>Erneut versuchen</Button>}
  </Space>;

  const facilitiesTab = <Space direction="vertical" size={16} style={{ width: "100%" }}>
    <Alert showIcon message="Einrichtungen überschreiben nur Ankunft Hinfahrt und Abholung Rückfahrt. Leere Felder übernehmen die Standardzeit." />
    <Space wrap>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => openFacility(null)}>Einrichtung anlegen</Button>
      <Button onClick={async () => {
        try {
          const res = await axios.post("/facilities/migrate-existing");
          clearRouteCache();
          message.success(`${res.data.assignedCount} Fahrgäste zugeordnet, ${res.data.createdCount} Einrichtungen angelegt.`);
          load();
        } catch { message.error("Vorhandene Arbeitsadressen konnten nicht übernommen werden."); }
      }}>Vorhandene Arbeitsadressen übernehmen</Button>
    </Space>
    <Table rowKey="id" columns={facilityColumns} dataSource={facilities} loading={loading} />
  </Space>;

  return <div>
    <h2 className="app-page-title">Planungsvorgaben</h2>
    <Space wrap style={{ marginBottom: 16 }}>
      <Button icon={<DownloadOutlined />} loading={exporting} onClick={exportExcel}>Excel herunterladen</Button>
      <Upload accept=".xlsx" showUploadList={false} customRequest={importExcel}>
        <Button icon={<UploadOutlined />} loading={importing}>Excel importieren</Button>
      </Upload>
      <Typography.Text type="secondary">Die Arbeitsmappe enthält Standardzeiten und Einrichtungen.</Typography.Text>
    </Space>
    <Tabs items={[{ key: "defaults", label: "Standardzeiten", children: standardTab }, { key: "facilities", label: "Einrichtungen", children: facilitiesTab }]} />
    <Modal open={facilityOpen} title={editingFacility ? "Einrichtung bearbeiten" : "Neue Einrichtung"} footer={null}
      width={900} destroyOnClose onCancel={() => setFacilityOpen(false)}>
      <Form form={facilityForm} layout="vertical" onFinish={saveFacility}>
        <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="address" label="Adresse" rules={[{ required: true, min: 5 }]}><Input /></Form.Item>
        <Form.Item name="notes" label="Notizen"><Input.TextArea /></Form.Item>
        <Table pagination={false} rowKey="weekday" dataSource={WEEKDAYS.map((weekday) => ({ weekday }))} columns={[
          { title: "Wochentag", dataIndex: "weekday", render: (v) => LABELS[v] },
          { title: "Ankunft Hinfahrt (leer = Standard)", render: (_, row) => <Form.Item name={`outbound_${row.weekday}`} noStyle><WindowPicker /></Form.Item> },
          { title: "Abholung Rückfahrt (leer = Standard)", render: (_, row) => <Form.Item name={`return_${row.weekday}`} noStyle><WindowPicker /></Form.Item> },
        ]} />
        <Button type="primary" htmlType="submit" style={{ marginTop: 16 }}>Speichern</Button>
      </Form>
    </Modal>
  </div>;
}
